import { Button, Heading, Span } from '@pcln/horizon'
import { RecentSelection } from '@pcln/horizon-customer-growth-components'
import { theme } from 'pcln-design-system'
import { useEffect, type Dispatch, type SetStateAction } from 'react'
import { useMediaQuery } from 'react-responsive'

import ComponentMetricsBoundary from '@/components/ComponentMetricsBoundary/ComponentMetricsBoundary'
import SimilarHotels from '@/components/TripActivity/SimilarHotels'
import useBootstrapData from '@/hooks/useBootstrapData'
import useClientSideSignedInData from '@/hooks/useClientSideSignedInData'
import useSeti from '@/hooks/useSeti'
import useSimilarHotels from '@/hooks/useSimilarHotels'

import ConditionalRender, { Experiment, Original } from '../ConditionalRender'
import { fireRecentSelectionLoad } from './ga4'
import useTripActivityData from './hooks/useTripActivityData'
import RecentSearchAndPriceWatchCarousel from './RecentSearchAndPriceWatchCarousel'
import RecentSearchCarousel from './RecentSearchCarousel'
import { recentSelectionMapper } from './recentSelectionMapper'
import { tripActivityVariants } from './TripActivity.variant'
import type {
  AbandonedSelection as AbandonedSelectionType,
  RecentSearchAndPriceWatchList,
  RecentTripSearches,
  SearchProductList,
} from './types'
import { onViewAllButtonClick } from './utils'

function isRecentTripSearches(
  _item: RecentSearchAndPriceWatchList,
  isPriceWatchVariant: boolean,
): _item is ReadonlyArray<RecentTripSearches> {
  return isPriceWatchVariant === false
}
export default function TripActivity({
  productType,
  setIsVisible,
  shouldUseSmallerHeading,
  abandonedSelectionData,
  hideAbandonedSelection,
  pageName,
}: {
  setIsVisible?: Dispatch<SetStateAction<boolean>>
  productType: SearchProductList
  shouldUseSmallerHeading?: boolean
  abandonedSelectionData?: AbandonedSelectionType | null
  hideAbandonedSelection?: boolean
  pageName: 'homepage' | 'landing'
}) {
  const {
    signInStatus: { signedIn: serverSignedIn },
    appName,
    appVersion,
    isMobile,
  } = useBootstrapData()
  const { signedIn: clientSideSignedIn } = useClientSideSignedInData()
  const signedIn = serverSignedIn || clientSideSignedIn

  const isDesktop = useMediaQuery({
    query: `(min-width: ${theme.breakpoints.lg as string})`,
  })
  const isShowPriceWatch =
    useSeti({ experimentCode: 'HP_PUWYLO_PRICEWATCH', shouldFireImpression: false }) === 'VARIANT'
  const { atMostFiveRecentSearchCards, abandonedSelection } = useTripActivityData(
    productType,
    abandonedSelectionData,
  )

  const abandonedHotelSelection = abandonedSelection?.productType === 'hotels' ? abandonedSelection : null
  const appCode = isMobile ? 'MOBILEWEB' : 'DESKTOP'

  const showSimilarHotels =
    useSeti({
      experimentCode: 'WEB_HP_HOTELSELECTION_SIMILARHOTELS',
      shouldFireImpression:
        !hideAbandonedSelection && abandonedHotelSelection !== null && pageName === 'homepage',
    }) === 'VARIANT' &&
    !hideAbandonedSelection &&
    abandonedHotelSelection !== null &&
    pageName === 'homepage'
  const { similarHotels, isLoading: isSimilarHotelsLoading } = useSimilarHotels({
    showSimilarHotels,
    abandonedHotelSelection,
    appCode,
    appName,
    appVersion,
  })
  const showAbandonedSelection = abandonedSelection && !hideAbandonedSelection

  useSeti({
    experimentCode: 'WEB_HP_PACKAGE_SELECTIONS',
    shouldFireImpression: Boolean(showAbandonedSelection) && productType?.includes('PKG'),
  })

  const hasRecentSearchCards = atMostFiveRecentSearchCards.length > 0
  useEffect(() => {
    if (setIsVisible) {
      setIsVisible(hasRecentSearchCards || abandonedSelection !== null)
    }
  }, [hasRecentSearchCards, abandonedSelection, setIsVisible, hideAbandonedSelection])

  useEffect(() => {
    if (showAbandonedSelection) {
      fireRecentSelectionLoad({ selection: abandonedSelection, pageName })
    }
  }, [showAbandonedSelection, abandonedSelection, pageName])

  const isViewAllButtonSeti =
    useSeti({
      experimentCode: 'WEB_HP_VIEWALLACTIVITY_BUTTON',
      shouldFireImpression: hasRecentSearchCards || Boolean(showAbandonedSelection),
    }) === 'VARIANT'

  const { heading, recentSearchesHeading, wrapperSlot, viewAllButton } = tripActivityVariants({
    useSmallerHeading: shouldUseSmallerHeading,
    isViewAllButtonSeti: isViewAllButtonSeti,
  })
  return hasRecentSearchCards || showAbandonedSelection ? (
    <ComponentMetricsBoundary componentId='TripActivity'>
      <div className='relative'>
        <div className={wrapperSlot()}>
          <Heading as='h2' palette='neutral' shade='13' className={heading()}>
            Pick up where you left off
          </Heading>
          {isDesktop && (
            <Button
              type='secondary'
              size='sm'
              slots={{ buttonSlot: viewAllButton() }}
              data-testid='view-all-button-top-right'
              onClick={() => onViewAllButtonClick(pageName)}
            >
              View all recent activity
            </Button>
          )}
        </div>
        <div className='flex flex-col gap-10 md:gap-12'>
          {showAbandonedSelection && (
            <div className='flex flex-col gap-6'>
              <div className='flex flex-col gap-2'>
                <RecentSelection {...recentSelectionMapper(abandonedSelection, { pageName })} />
                {abandonedHotelSelection && (
                  <Span palette='neutral' shade='13' className='text-body3'>
                    *Availability and pricing are subject to change
                  </Span>
                )}
              </div>
              {showSimilarHotels && similarHotels.length > 0 && (
                <SimilarHotels
                  similarHotels={similarHotels}
                  isLoading={isSimilarHotelsLoading}
                  pageName={pageName}
                  useSmallerHeading={shouldUseSmallerHeading}
                />
              )}
            </div>
          )}
          {hasRecentSearchCards && (
            <div className='flex flex-col gap-4'>
              {showAbandonedSelection && (
                <Heading as='h3' palette='neutral' shade='13' className={recentSearchesHeading()}>
                  Your recent searches
                </Heading>
              )}
              <ConditionalRender
                experimentCode='HP_PUWYLO_PRICEWATCH'
                triggerExperiment={() => {
                  return signedIn
                }}
              >
                <Original>
                  <RecentSearchCarousel
                    items={
                      isRecentTripSearches(atMostFiveRecentSearchCards, isShowPriceWatch)
                        ? atMostFiveRecentSearchCards
                        : []
                    }
                    pageName={pageName}
                  />
                </Original>
                <Experiment>
                  <RecentSearchAndPriceWatchCarousel
                    items={
                      !isRecentTripSearches(atMostFiveRecentSearchCards, isShowPriceWatch)
                        ? atMostFiveRecentSearchCards
                        : []
                    }
                    pageName={pageName}
                  />
                </Experiment>
              </ConditionalRender>
            </div>
          )}
        </div>
        {!isDesktop && (
          <Button
            type='secondary'
            size='sm'
            className='mt-4 w-full'
            data-testid='view-all-button-bottom'
            onClick={() => onViewAllButtonClick(pageName)}
          >
            View all recent activity
          </Button>
        )}
      </div>
    </ComponentMetricsBoundary>
  ) : null
}
